/**
 * Shared hooks: the server clock, geolocation, and the two external stores.
 */
import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { getSyncStatus, subscribeSync, type SyncStatus } from './sync-status';
import { outboxSnapshot, subscribeOutbox, type OutboxRecord } from './outbox';

/** Connectivity + queue depth, published by the api client and the outbox. */
export function useSyncState(): SyncStatus {
  return useSyncExternalStore(subscribeSync, getSyncStatus, getSyncStatus);
}

/** Everything still waiting to reach the server, oldest first. */
export function useOutbox(): readonly OutboxRecord[] {
  return useSyncExternalStore(subscribeOutbox, outboxSnapshot, outboxSnapshot);
}

/**
 * A clock that ticks locally but is ANCHORED to the server.
 *
 * WHY not `Date.now()`: the whole attendance module exists because phone clocks
 * lie. Showing "đã làm 7:32:10" computed from a device clock that is twenty
 * minutes fast would contradict the payslip. So the app takes `serverTime` from
 * the last `/staff/home` response, measures elapsed time with
 * `performance.now()` (monotonic, immune to the user changing the clock), and
 * adds the two. The displayed number therefore always agrees with the server's
 * view, drifting only by real elapsed seconds.
 *
 * @param serverTime the `serverTime` field of the most recent response
 * @param intervalMs how often to re-render; 1000 for a seconds display
 */
export function useServerClock(serverTime: number | undefined, intervalMs = 1000): number {
  const anchor = useRef<{ server: number; mono: number } | null>(null);
  const [now, setNow] = useState<number>(() => serverTime ?? Date.now());

  if (serverTime !== undefined && (anchor.current === null || anchor.current.server !== serverTime)) {
    anchor.current = { server: serverTime, mono: performance.now() };
  }

  useEffect(() => {
    const tick = (): void => {
      const a = anchor.current;
      setNow(a === null ? Date.now() : a.server + (performance.now() - a.mono));
    };
    tick();
    const timer = window.setInterval(tick, intervalMs);
    return () => window.clearInterval(timer);
  }, [intervalMs, serverTime]);

  return now;
}

// ---------------------------------------------------------------------------
// Geolocation
// ---------------------------------------------------------------------------

export interface GeoFix {
  readonly latitude: number;
  readonly longitude: number;
  readonly accuracyM: number;
  /** Device clock at the moment of the fix — forensics only. */
  readonly at: number;
}

export type GeoStatus = 'idle' | 'locating' | 'ready' | 'denied' | 'unavailable' | 'timeout';

export interface GeoState {
  readonly status: GeoStatus;
  readonly fix: GeoFix | null;
  readonly message: string | null;
}

const GEO_MESSAGES: Readonly<Record<Exclude<GeoStatus, 'idle' | 'locating' | 'ready'>, string>> = {
  denied:
    'Bạn đã từ chối quyền vị trí. Chấm công vẫn gửi được nhưng sẽ bị đánh dấu cần quản lý xác minh. Bật lại quyền vị trí trong cài đặt trình duyệt để tránh việc này.',
  unavailable: 'Thiết bị không lấy được vị trí. Hãy ra chỗ thoáng hoặc bật Wi-Fi rồi thử lại.',
  timeout: 'Lấy vị trí quá lâu. Bạn vẫn có thể chấm công, hệ thống sẽ ghi nhận là thiếu vị trí.',
};

/**
 * One-shot, user-initiated position request.
 *
 * NOT a watch: continuous tracking of staff is neither needed nor acceptable —
 * a position is captured only at the moment someone taps a chấm công button,
 * and only to answer one question ("were you at the branch?").
 *
 * A refusal or a failure NEVER blocks the action. The server records the event
 * with an OUT_OF_GEOFENCE / LOW_GPS_ACCURACY risk flag and routes it to review;
 * refusing to let someone clock in because a basement has no GPS would be worse
 * than a flagged event.
 */
export function useGeolocation(): {
  state: GeoState;
  request: () => Promise<GeoFix | null>;
} {
  const [state, setState] = useState<GeoState>({ status: 'idle', fix: null, message: null });

  const request = useCallback((): Promise<GeoFix | null> => {
    if (typeof navigator === 'undefined' || navigator.geolocation === undefined) {
      setState({ status: 'unavailable', fix: null, message: GEO_MESSAGES.unavailable });
      return Promise.resolve(null);
    }

    setState((prev) => ({ ...prev, status: 'locating', message: null }));

    return new Promise<GeoFix | null>((resolve) => {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const fix: GeoFix = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracyM: position.coords.accuracy,
            at: Date.now(),
          };
          setState({ status: 'ready', fix, message: null });
          resolve(fix);
        },
        (error) => {
          const status: GeoStatus =
            error.code === error.PERMISSION_DENIED
              ? 'denied'
              : error.code === error.TIMEOUT
                ? 'timeout'
                : 'unavailable';
          setState({
            status,
            fix: null,
            message: GEO_MESSAGES[status as keyof typeof GEO_MESSAGES],
          });
          resolve(null);
        },
        {
          enableHighAccuracy: true,
          // 12s: long enough for a cold GPS fix outdoors, short enough that an
          // employee is not left staring at a spinner at the staff entrance.
          timeout: 12_000,
          // A fix from the last 30s is fine and saves the radio warm-up.
          maximumAge: 30_000,
        },
      );
    });
  }, []);

  return { state, request };
}
